import { Component, OnDestroy, OnInit, Output, EventEmitter, Input } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ComplementaryInfoContract, ComplementaryInfoForm, QuestionPercentage } from "@edp-models/complementary-info.model";
import { GetComplementaryInfo, SendComplementaryInfoForm } from "@edp-store/actions/contract.actions";
import { SelectOverlayAction, SetNeedLoadUserPoints } from "@edp-store/actions/layout.actions";
import { getComplementaryInfo } from "@edp-store/selectors/contract.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { requireCheckboxesToBeCheckedValidator } from "@edp-validators/require-checkboxes-to-be-checked.validator";
import { select, Store } from "@ngrx/store";
import { combineLatest, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { environment } from "@edp-environments/environment";
import { GoogleAnalyticsService } from "@edp-services/google-analytics.service";
import { getContractEfficiency } from "@edp-store/selectors/energy-efficiency.selectors";
import { ContractEfficiency } from "@edp-models/energy-efficiency.model";

@Component({
    selector: "edp-overlay-more-info",
    templateUrl: "./edp-overlay-more-info.component.html",
    styleUrls: ["./edp-overlay-more-info.component.scss"],
})
export class EdpOverlayMoreInfoComponent implements OnInit, OnDestroy {
    @Input() public origin: string;
    @Output() public closeSideBar = new EventEmitter<boolean>();
    public complementaryInfo$: Observable<ComplementaryInfoContract[]>;
    public complementaryInfo: ComplementaryInfoContract[];
    public step = 1;
    public selectedContract: any;
    public form: FormGroup;
    public gasSubForm = false;
    public waterSubForm = false;
    public electricCarSubForm = false;
    public selectedContractIndex = 0;
    public pgPerQuestion: number;
    public formObject: ComplementaryInfoForm;
    public questionPercentage: QuestionPercentage;
    public questionPercentageTotal = 0;
    public showLoader = true;
    public formSubmitted = false;
    private unsubscribe = new Subject();
    private contractEfficiency$: Observable<ContractEfficiency>;

    public constructor(private store: Store<IAppState>, private fb: FormBuilder, private googleAnalyticsService: GoogleAnalyticsService) {
        this.complementaryInfo$ = this.store.pipe(takeUntil(this.unsubscribe), select(getComplementaryInfo));
        this.contractEfficiency$ = this.store.pipe(takeUntil(this.unsubscribe), select(getContractEfficiency));
    }

    public ngOnInit(): void {
        combineLatest([
            this.contractEfficiency$,
            this.complementaryInfo$,
        ]).subscribe(([contractEfficiency, contractList]) => {
            if (contractList) {
                this.complementaryInfo = contractList;
                let _foundIndex = -1;

                if (contractEfficiency && this.origin === "energyEfficiency") {
                    _foundIndex = this.complementaryInfo.findIndex(
                        contract => contract.contractId === contractEfficiency.contractId);
                }
                if (_foundIndex > -1) {
                    this.step = 2;
                    this.selectContract(_foundIndex);
                } else if (this.complementaryInfo.length === 1) {
                    const singleContract = this.complementaryInfo[0];
                    if (singleContract.percentage !== 100) {
                        this.selectContract(0);
                    }
                }
                this.showLoader = false;
                this.formSubmitted = false;

                if (this.step === 2 && this.origin !== "energyEfficiency") {
                    this.nextContract();
                }
            }
        });

        this.store.dispatch(new GetComplementaryInfo());
    }

    public selectOverlay(type: string): void {
        if (environment.isApp) {
            this.closeSideBar.emit(true);
            this.store.dispatch(new SelectOverlayAction(null));
        } else {
            this.store.dispatch(new SelectOverlayAction(type));
        }
    }

    public selectContract(contractIndex) {
        this.selectedContract = this.complementaryInfo[contractIndex];
        if (this.complementaryInfo.length === 1) {
            this.step = 2;
        }

        this.pgPerQuestion = this.selectedContract.supplyType !== "E" ? 100 / 5 : 100 / 6;

        this.buildForm();
        this.setValidators();
        this.initQuestions();
        this.calcTotal();
    }

    public setValidators() {
        this.form.get("hasWaterBomb").valueChanges.subscribe(hasWaterBomb => {
            this.questionPercentage.question5 = this.pgPerQuestion;
            this.calcTotal();

            if (hasWaterBomb === "1") {
                this.form.get("waterCheckboxGroup").setValidators(requireCheckboxesToBeCheckedValidator());
                this.formSubmitted = false;
            } else {
                this.form.get("waterCheckboxGroup").setValidators(null);
            }
            this.form.get("waterCheckboxGroup").updateValueAndValidity();
        });

        this.form.get("hasGas").valueChanges.subscribe(hasGas => {
            this.questionPercentage.question4 = this.pgPerQuestion;
            this.calcTotal();

            if (hasGas === "1") {
                this.form.get("gasCheckboxGroup").setValidators(requireCheckboxesToBeCheckedValidator());
                this.formSubmitted = false;
            } else {
                this.form.get("gasCheckboxGroup").setValidators(null);
            }
            this.form.get("gasCheckboxGroup").updateValueAndValidity();
        });

        this.form.get("electricCar").valueChanges.subscribe(electricCar => {
            this.questionPercentage.question6 = this.pgPerQuestion;
            this.calcTotal();

            if (electricCar === "1") {
                this.form.get("chargeElectricCarHome").setValidators([Validators.required]);
                this.form.get("chargeElectricCarHome").setValue(null);
                this.formSubmitted = false;
                this.electricCarSubForm = true;
            } else {
                this.form.get("chargeElectricCarHome").setValidators(null);
                this.electricCarSubForm = false;
            }
            this.form.get("chargeElectricCarHome").updateValueAndValidity();
        });

        this.form.get("housingType").valueChanges.subscribe(() => {
            this.questionPercentage.question1 = this.pgPerQuestion;
            this.calcTotal();
        });

        this.form.get("utilization").valueChanges.subscribe(() => {
            this.questionPercentage.question2 = this.pgPerQuestion;
            this.calcTotal();
        });

        this.form.get("residents").valueChanges.subscribe(() => {
            this.questionPercentage.question3 = this.pgPerQuestion;
            this.calcTotal();
        });
    }

    public initQuestions() {
        this.questionPercentage = {
            question1: this.selectedContract.housingType !== null ? this.pgPerQuestion : 0,
            question2: this.selectedContract.utilization !== null ? this.pgPerQuestion : 0,
            question3: this.selectedContract.residents !== null ? this.pgPerQuestion : 0,
            question4: this.selectedContract.hasGas !== null ? this.pgPerQuestion : 0,
            question5: this.selectedContract.hasWaterBomb !== null ? this.pgPerQuestion : 0,
            question6: this.selectedContract.electricCar !== null ? this.pgPerQuestion : 0,
        };
    }

    public calcTotal() {
        this.questionPercentageTotal = 0;
        for (const item in this.questionPercentage) {
            if (this.questionPercentage[item]) {
                const currentTotal = this.questionPercentageTotal + this.questionPercentage[item];
                this.questionPercentageTotal = currentTotal > 100 ? 100 : currentTotal;
            }
        }
        this.questionPercentageTotal = Math.round(this.questionPercentageTotal);
    }

    public buildForm() {
        this.gasSubForm = false;
        this.waterSubForm = false;
        this.electricCarSubForm = false;

        this.form = this.fb.group({
            housingType: new FormControl(this.selectedContract.housingType),
            utilization: new FormControl(this.selectedContract.utilization),
            residents: new FormControl(this.selectedContract.residents),
            hasGas: new FormControl(this.selectedContract.hasGas),
            gasCheckboxGroup: new FormGroup({
                hasGasKitchen: new FormControl(this.selectedContract.hasGasKitchen),
                hasGasWaterHeating: new FormControl(this.selectedContract.hasGasWaterHeating),
                hasGasHeating: new FormControl(this.selectedContract.hasGasHeating),
            }),
            hasWaterBomb: new FormControl(this.selectedContract.hasWaterBomb),
            waterCheckboxGroup: new FormGroup({
                hasWaterBombPool: new FormControl(this.selectedContract.hasWaterBombPool),
                hasWaterBombGardening: new FormControl(this.selectedContract.hasWaterBombGardening),
                hasWaterBombHydropressure: new FormControl(this.selectedContract.hasWaterBombHydropressure),
            }),
            electricCar: new FormControl(this.selectedContract.electricCar),
            chargeElectricCarHome: new FormControl(this.selectedContract.chargeElectricCarHome),
        });

        if (this.selectedContract.hasGas === "1") {
            this.gasSubForm = true;
        }

        if (this.selectedContract.hasWaterBomb === "1") {
            this.waterSubForm = true;
        }

        if (this.selectedContract.electricCar === "1") {
            this.electricCarSubForm = true;
        }
    }

    public submitForm() {
        this.formSubmitted = true;
        this.formObject = this.form.value;
        this.formObject.contractId = this.selectedContract.contractId;

        if (this.form.valid) {
            this.store.dispatch(new SendComplementaryInfoForm(this.formObject));
            this.showLoader = true;
            this.googleAnalyticsService.datalayerEvent("CONTRACT_INFORMATION_SUBMITED");
            this.store.dispatch(new SetNeedLoadUserPoints(true));
        }
    }

    public nextContract() {
        let completed = 0;

        for (const {} of this.complementaryInfo) {
            this.selectedContractIndex++;
            if (this.selectedContractIndex >= this.complementaryInfo.length) {
                this.selectedContractIndex = 0;
            }

            if (this.complementaryInfo[this.selectedContractIndex].percentage !== 100) {
                this.selectContract(this.selectedContractIndex);
                break;
            } else {
                completed++;
                if (completed === this.complementaryInfo.length) {
                    this.step = 1;
                }
            }
        }
    }

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
